import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Users, 
  BarChart3, 
  ChevronDown, 
  ChevronUp,
  Edit,
  Trash2,
  Calendar,
  Euro
} from 'lucide-react';
import { 
  getDepensesPartagees, 
  deleteDepensePartagee 
} from '../lib/services/depensesPartageesService';
import { getParticipants } from '../lib/services/participantsService';
import { 
  DepensePartagee, 
  DepensesByMonth 
} from '../types/shared-expenses';

const SharedExpenses: React.FC = () => {
  const navigate = useNavigate();
  const [depensesGroupedByMonth, setDepensesGroupedByMonth] = useState<DepensesByMonth[]>([]);
  const [loading, setLoading] = useState(true);
  const [participantsCount, setParticipantsCount] = useState(0);
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Charger toutes les dépenses partagées
      const { data: depensesData, error: depensesError } = await getDepensesPartagees();
      
      if (depensesError) {
        throw depensesError;
      }
      
      if (depensesData && depensesData.length > 0) {
        // Regrouper les dépenses par mois
        const depensesByMonth: Record<string, DepensePartagee[]> = {};
        
        depensesData.forEach((depense: DepensePartagee) => {
          const date = new Date(depense.date);
          const moisKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          
          if (!depensesByMonth[moisKey]) {
            depensesByMonth[moisKey] = [];
          }
          
          depensesByMonth[moisKey]!.push(depense);
        });
        
        // Convertir en tableau pour l'affichage et calculer les totaux
        const groupedData: DepensesByMonth[] = Object.entries(depensesByMonth).map(([moisKey, depenses]) => {
          const [annee, mois] = moisKey.split('-');
          if (!annee || !mois) return null;
          const date = new Date(parseInt(annee), parseInt(mois) - 1, 1);
          const moisLabel = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
          
          // Calculer le total des dépenses pour ce mois
          const total = depenses.reduce((sum, depense) => sum + depense.montant_total, 0);
          
          return {
            mois: moisKey,
            moisLabel: moisLabel.charAt(0).toUpperCase() + moisLabel.slice(1),
            depenses,
            total
          };
        }).filter((item): item is DepensesByMonth => item !== null);
        
        // Trier par mois décroissant (du plus récent au plus ancien)
        groupedData.sort((a, b) => b.mois.localeCompare(a.mois));
        
        setDepensesGroupedByMonth(groupedData);
      } else {
        setDepensesGroupedByMonth([]);
      }
      
      // Charger le nombre de participants
      const { data: participantsData, error: participantsError } = await getParticipants();
      
      if (participantsError) {
        throw participantsError;
      }
      
      if (participantsData) {
        setParticipantsCount(participantsData.length);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      // Gérer l'erreur de manière appropriée
    } finally {
      setLoading(false);
    }
  };

  const formatTotalAmount = (amount: number) => {
    return amount.toLocaleString('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const formatAmount = (amount: number) => {
    return amount.toLocaleString('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    });
  };

  const toggleMonthExpansion = (moisKey: string) => {
    setExpandedMonths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(moisKey)) {
        newSet.delete(moisKey);
      } else {
        newSet.add(moisKey);
      }
      return newSet;
    });
  };

  const handleDeleteDepense = async (id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette dépense partagée ?')) {
      try {
        const { error } = await deleteDepensePartagee(id);
        if (error) throw error;
        
        // Recharger les données
        await loadData();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Erreur lors de la suppression de la dépense partagée');
      }
    }
  };

  const navigateToSynthese = () => {
    const now = new Date();
    const mois = now.getMonth() + 1;
    const annee = now.getFullYear();
    
    navigate(`/shared-expenses/summary/${annee}/${mois.toString().padStart(2, '0')}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-dark-300">Chargement des dépenses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Dépenses Partagées</h1>
          <p className="text-dark-300 mt-2">Gérez vos dépenses partagées avec vos participants</p>
        </div>
      </div>

      {/* Boutons d'action */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          onClick={() => navigate('/participants')}
          className="flex items-center justify-center space-x-3 bg-dark-800 hover:bg-dark-700 text-white px-6 py-4 rounded-lg transition-colors duration-200"
        >
          <Users className="w-6 h-6" />
          <span className="font-medium">Participants ({participantsCount})</span>
        </button>
        
        <button
          onClick={navigateToSynthese}
          className="flex items-center justify-center space-x-3 bg-dark-800 hover:bg-dark-700 text-white px-6 py-4 rounded-lg transition-colors duration-200"
        >
          <BarChart3 className="w-6 h-6" />
          <span className="font-medium">Synthèse</span>
        </button>
      </div>

      {/* Liste des dépenses partagées regroupées par mois */}
      {depensesGroupedByMonth.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-dark-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-10 h-10 text-dark-400" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">Aucune dépense partagée</h3>
          <p className="text-dark-300 mb-6">
            Ajoutez votre première dépense partagée en cliquant sur le bouton + ci-dessous.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {depensesGroupedByMonth.map((monthData) => {
            const isExpanded = expandedMonths.has(monthData.mois);
            
            return (
              <div key={monthData.mois} className="bg-dark-900 rounded-lg overflow-hidden">
                {/* En-tête du mois avec le total et bouton de pliage */}
                <button
                  onClick={() => toggleMonthExpansion(monthData.mois)}
                  className="w-full flex items-center justify-between p-6 bg-dark-800 hover:bg-dark-700 transition-colors duration-200"
                >
                  <div className="flex items-center space-x-4">
                    <h3 className="text-xl font-semibold text-primary-500">
                      {monthData.moisLabel}
                    </h3>
                    <div className="flex items-center space-x-2 text-white">
                      <Euro className="w-5 h-5" />
                      <span className="font-bold text-lg">
                        {formatTotalAmount(monthData.total)}
                      </span>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-6 h-6 text-primary-500" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-primary-500" />
                  )}
                </button>
                
                {/* Liste des dépenses pour ce mois (affichée seulement si déplié) */}
                {isExpanded && (
                  <div className="p-6 space-y-4">
                    {monthData.depenses.map((depense) => (
                      <div key={depense.id} className="bg-dark-800 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <span className="text-primary-500 font-medium">
                                {formatDate(depense.date)}
                              </span>
                              <span className="text-white font-bold text-lg">
                                {formatAmount(depense.montant_total)}
                              </span>
                            </div>
                            <h4 className="text-white text-lg font-medium">
                              {depense.description}
                            </h4>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => navigate(`/shared-expenses/edit/${depense.id}`)}
                              className="p-2 text-dark-300 hover:text-primary-500 hover:bg-dark-700 rounded-lg transition-colors duration-200"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteDepense(depense.id)}
                              className="p-2 text-dark-300 hover:text-red-500 hover:bg-dark-700 rounded-lg transition-colors duration-200"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        
                        {/* Affichage des participations */}
                        {depense.participations && depense.participations.length > 0 && (
                          <div className="border-t border-dark-700 pt-3">
                            <h5 className="text-dark-300 text-sm font-medium mb-2">Participants :</h5>
                            <div className="space-y-2">
                              {depense.participations.map((participation) => (
                                <div key={participation.id} className="flex items-center justify-between text-sm">
                                  <span className="text-dark-300">
                                    {participation.participant?.nom || 'Participant inconnu'}
                                  </span>
                                  <span className={`font-medium ${
                                    participation.montant_paye === 0 
                                      ? 'text-dark-400' 
                                      : 'text-primary-500'
                                  }`}>
                                    {participation.montant_paye === 0 
                                      ? "N'a pas payé" 
                                      : formatAmount(participation.montant_paye)
                                    }
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Bouton d'ajout */}
      <button
        onClick={() => {
          if (participantsCount === 0) {
            if (window.confirm(
              'Vous devez d\'abord ajouter des participants avant de créer une dépense partagée. Voulez-vous ajouter des participants maintenant ?'
            )) {
              navigate('/participants');
            }
          } else {
            navigate('/shared-expenses/add');
          }
        }}
        className="fixed bottom-8 right-8 w-16 h-16 bg-primary-500 hover:bg-primary-600 text-white rounded-full flex items-center justify-center shadow-lg transition-colors duration-200 hover:scale-110"
      >
        <Plus className="w-8 h-8" />
      </button>
    </div>
  );
};

export default SharedExpenses; 